// src/components/gallery/PhotoModal.tsx
/**
 * フルスクリーン画像表示モーダル
 * XOR-Pix画像を大きく表示し、いいね・シェア機能を提供
 */
import React, { useEffect, useCallback, useState, useMemo } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { GalleryImage } from '../../services/characterService';
import XorPixViewer from '../xorpix/XorPixViewer';
import LikeButton from '../ui/LikeButton';

interface PhotoModalProps {
  isOpen: boolean;
  onClose: () => void;
  image: GalleryImage | null;
}

const PhotoModal: React.FC<PhotoModalProps> = ({
  isOpen,
  onClose,
  image,
}) => {
  const [showControls, setShowControls] = useState(true);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });

  // ESCキーで閉じる
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
      setShowControls(true);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  // ウィンドウサイズの更新
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // コンテナスタイルを計算
  const containerStyle = useMemo(() => {
    if (!image) return {};

    const aspectRatio = image.width / image.height;
    const maxWidth = windowSize.width * 0.9;
    const maxHeight = windowSize.height * 0.85;

    let width: number;
    let height: number;

    if (aspectRatio > maxWidth / maxHeight) {
      // 横長: 幅を基準
      width = Math.min(maxWidth, image.width);
      height = width / aspectRatio;
    } else {
      // 縦長: 高さを基準
      height = Math.min(maxHeight, image.height);
      width = height * aspectRatio;
    }

    return {
      width: `${width}px`,
      height: `${height}px`,
    };
  }, [image, windowSize]);

  // 画像エリアクリックでUIトグル
  const handleViewerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowControls(!showControls);
  };

  // シェアURL生成
  const getShareUrl = (): string => {
    if (!image) return '';
    const isProduction = import.meta.env.VITE_API_BASE_URL === 'https://api.figryne.com';
    const bucketName = isProduction ? 'figryne-public-assets-prod' : 'figryne-public-assets';
    return isProduction
      ? `https://figryne.com/share/pix/${image.modelId}`
      : `https://storage.googleapis.com/${bucketName}/share/pix/${image.modelId}.html`;
  };

  // X (Twitter) シェア
  const handleShareX = () => {
    if (!image || (!image.isPublic && !image.isMembersOnly)) return;
    const shareUrl = getShareUrl();
    const title = image.title || 'Untitled';
    const text = `${title} | ORIFOLI`;
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  // Bluesky シェア
  const handleShareBluesky = () => {
    if (!image || (!image.isPublic && !image.isMembersOnly)) return;
    const shareUrl = getShareUrl();
    const title = image.title || 'Untitled';
    const text = `${title} | ORIFOLI`;
    window.open(
      `https://bsky.app/intent/compose?text=${encodeURIComponent(text + ' ' + shareUrl)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  if (!isOpen || !image) {
    return null;
  }

  const isShareDisabled = !image.isPublic && !image.isMembersOnly;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black"
      onClick={onClose}
    >
      {/* 閉じるボタン */}
      {showControls && (
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 bg-black/50 hover:bg-black/70 rounded-full transition-colors"
          aria-label="Close"
        >
          <XMarkIcon className="w-6 h-6 text-white" />
        </button>
      )}

      {/* ドラッグインジケーター */}
      {showControls && (
        <div className="absolute top-5 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-white/50 rounded-full z-10" />
      )}

      {/* 画像表示エリア */}
      <div
        className="relative flex items-center justify-center overflow-hidden"
        style={containerStyle}
        onClick={handleViewerClick}
      >
        <XorPixViewer
          noise1Url={image.noise1Url}
          noise2Url={image.noise2Url}
          rid={image.rid}
          width={image.width}
          height={image.height}
          characterId={image.modelId}
          fallbackNoise1Url={image.fallbackNoise1Url}
          fallbackNoise2Url={image.fallbackNoise2Url}
          fitMode="contain"
          className="w-full h-full"
        />
      </div>

      {/* 下部アクションバー */}
      {showControls && (
        <div className="fixed bottom-0 left-0 right-0 py-4 pb-8 px-6">
          <div className="max-w-5xl mx-auto flex justify-center gap-12 items-center">
            {/* いいねボタン */}
            <LikeButton
              modelId={image.modelId}
              initialCount={image.likeCount || 0}
              className="flex items-center gap-1 text-sm"
              iconClassName="w-5 h-5"
              textClassName="text-gray-400"
              disabled={isShareDisabled}
            />

            {/* X シェアボタン */}
            <button
              onClick={handleShareX}
              disabled={isShareDisabled}
              className={`flex items-center justify-center w-8 h-8 transition-colors ${
                isShareDisabled
                  ? 'text-gray-600 cursor-not-allowed'
                  : 'text-gray-400 hover:text-gray-200'
              }`}
              aria-label="Share on X"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </button>

            {/* Bluesky シェアボタン */}
            <button
              onClick={handleShareBluesky}
              disabled={isShareDisabled}
              className={`flex items-center justify-center w-8 h-8 transition-colors ${
                isShareDisabled
                  ? 'text-gray-600 cursor-not-allowed'
                  : 'text-gray-400 hover:text-[#0085ff]'
              }`}
              aria-label="Share on Bluesky"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.022-.276.04-.415.056-3.912.58-7.387 2.005-2.83 7.078 5.013 5.19 6.87-1.113 7.823-4.308.953 3.195 2.05 9.271 7.733 4.308 4.267-4.308 1.172-6.498-2.74-7.078a8.741 8.741 0 0 1-.415-.056c.14.017.279.036.415.056 2.67.297 5.568-.628 6.383-3.364.246-.828.624-5.79.624-6.478 0-.69-.139-1.861-.902-2.206-.659-.298-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8Z"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* 画像タイトル（オプション） */}
      {showControls && image.title && image.title !== 'Untitled' && (
        <div className="absolute bottom-20 left-1/2 -translate-x-1/2 px-4 py-2 bg-black/50 rounded-full">
          <p className="text-white text-sm">{image.title}</p>
        </div>
      )}
    </div>
  );
};

export default PhotoModal;
